import React, { useState } from 'react';
import { Modal, Button, Card, List, Alert, Spin, Tag } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { AdditionalQuestionnaire } from '../shared/types';

interface AdditionalQuestionnairesProps {
  questionnaires: AdditionalQuestionnaire[];
  loading: boolean;
  error: string | null;
  client: any;
}

const AdditionalQuestionnaires: React.FC<AdditionalQuestionnairesProps> = ({
  questionnaires,
  loading,
  error,
  client,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<AdditionalQuestionnaire | null>(null);
  const [showQuestionnaires, setShowQuestionnaires] = useState(false);

  const toggleQuestionnaires = () => {
    setShowQuestionnaires(!showQuestionnaires);
  };

  const openQuestionnaire = (questionnaire: AdditionalQuestionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setIsModalVisible(true);
  };

  const handleClose = () => {
    setIsModalVisible(false);
    setSelectedQuestionnaire(null);
  };

  const getIframeUrl = (questionnaireId: string): string => {
    const baseUrl = process.env.REACT_APP_QUESTIONNAIRE_UI;
    const sessionId = client?.id || 'unknown';
    const clientId = client?.id || 'unknown';
    const email = client?.email || '';

    return `${baseUrl}/questionnaire/${questionnaireId}?clientId=${clientId}&email=${encodeURIComponent(email)}&sessionId=${encodeURIComponent(sessionId)}`;
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'ethos':
        return 'blue';
      case 'recommended':
        return 'green';
      case 'heta':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'ethos':
        return 'Ethos Scan';
      case 'recommended':
        return 'Recommended';
      case 'heta':
        return 'HETA';
      default:
        return category;
    }
  };

  // Group questionnaires by category
  const groupedQuestionnaires = questionnaires.reduce((acc, q) => {
    if (!acc[q.category]) {
      acc[q.category] = [];
    }
    acc[q.category].push(q);
    return acc;
  }, {} as Record<string, AdditionalQuestionnaire[]>);

  return (
    <>
      <Card
        title="Additional Questionnaires"
        style={{ marginBottom: 16, marginTop: 16 }}
        extra={
          <Button 
            type="text" 
            onClick={toggleQuestionnaires}
            size="small"
          >
            {showQuestionnaires ? 'Hide' : 'Show'} ({questionnaires.length})
          </Button>
        }
      >
        {showQuestionnaires && (
          <div>
            {/* Loading indicator */}
            {loading && (
              <div style={{ marginBottom: 16, textAlign: 'center' }}>
                <Spin size="small" /> 
                <span style={{ marginLeft: 8 }}>Loading AI recommendations...</span>
              </div>
            )}

            {/* Error alert */}
            {error && (
              <Alert
                message="Error loading AI recommendations"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Display questionnaires */}
            {!loading && !error && questionnaires.length > 0 && (
              <>
                <Alert
                  message="Analysis Complete"
                  description="Based on the analysis, the following questionnaires are recommended. Click on any questionnaire to begin."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                {Object.entries(groupedQuestionnaires).map(([category, items]) => (
                  <div key={category} style={{ marginBottom: 24 }}>
                    <h4 style={{ 
                      marginBottom: 12, 
                      fontSize: '14px', 
                      fontWeight: 'bold',
                      color: '#1890ff'
                    }}>
                      {getCategoryLabel(category)}
                    </h4>
                    <List
                      dataSource={items}
                      renderItem={(questionnaire) => (
                        <List.Item
                          style={{ 
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            padding: '12px 16px',
                            borderRadius: '4px',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onClick={() => openQuestionnaire(questionnaire)}
                        >
                          <List.Item.Meta
                            avatar={<FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />}
                            title={
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>{questionnaire.label}</span>
                              </div>
                            }
                            description={
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {questionnaire.source_ie && (
                                  <div>Source: {questionnaire.source_ie}</div>
                                )}
                                <div style={{ marginTop: '4px', color: '#1890ff' }}>
                                  Click to start questionnaire →
                                </div>
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                ))}
              </>
            )}

            {/* No data message */}
            {!loading && !error && questionnaires.length === 0 && (
              <Alert
                message="No Recommendations Available"
                description="No analysis recommendations found for this client. Please ensure the client has completed the initial questionnaire."
                type="warning"
                showIcon
              />
            )}
          </div>
        )}
      </Card>

      {/* Modal for questionnaire iframe */}
      <Modal
        title={selectedQuestionnaire?.label || 'Questionnaire'}
        open={isModalVisible}
        onCancel={handleClose}
        footer={null}
        width="100%"
        style={{ top: 0, padding: 0, height: '100vh' }}
        bodyStyle={{ padding: 0, height: '100%' }}
        destroyOnClose
      >
        {selectedQuestionnaire && (
          <iframe
            src={getIframeUrl(selectedQuestionnaire.id)}
            title={selectedQuestionnaire.label}
            width="100%"
            height="700px"
            style={{ border: 'none' }}
          />
        )}
      </Modal>
    </>
  );
};

export default AdditionalQuestionnaires;
